/*
 * ATTENTION!!!!!!!!!!!!!!!!!
 *
 *
 *
 * The fields commented out are fields that are still in WIP
 * These changes are related to the new Raptor Job creation flow.
 * Those will be included in the next push.
 */

import { Box, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useState } from 'react';
import { useFormikContext } from 'formik';
import FormikAutocomplete, { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { FormikTextField } from '@/common/presentation/components/forms/fields/FormikTextField';
import { RaptorJobFormValues } from '../../../../RaptorJobValidationSchema';
import { useGetJobTypes } from '@/raptorjob/application/adapters/useJobType';
import { JobSiteAutocompleteOption, useListJobSitesAutocomplete } from '@/jobs/application/adapters/listJobSitesAutocomplete';
// import useAvailableVendors from '@/raptorjob/application/adapters/useAvailableVendors';
// import useAvailableSpecialFlags from '@/raptorjob/application/adapters/useAvailableSpecialFlags';
// import useAvailableExtraSystems from '@/raptorjob/application/adapters/useAvailableExtraSystems';
import tid from '@/common/application/utils/tid';
import { FormikCheckbox } from '@/common/presentation/components/forms/fields/FormikCheckbox';
import useAvailableVendors from '@/raptorjob/application/adapters/useAvailableVendors';

type JobTypeAutocompleteOption = NonNullable<NonNullable<NonNullable<ReturnType<typeof useGetJobTypes>['data']>['data']>>[number];

interface JobDetailsSectionProps {
  jobSiteSearchTerm: string;
  onJobSiteSearchTermChange: (value: string) => void;
  onOpenCreateJobSiteModal: () => void;
}

export function JobDetailsSection({ jobSiteSearchTerm, onJobSiteSearchTermChange, onOpenCreateJobSiteModal }: JobDetailsSectionProps) {
  const { values } = useFormikContext<RaptorJobFormValues>();

  const { data: jobTypes, isLoading: jobTypesLoading } = useGetJobTypes();
  const {
    options: jobSiteOptions,
    loading: jobSiteLoading,
    filterOptions: jobSiteFilterOptions
  } = useListJobSitesAutocomplete(jobSiteSearchTerm, 1000, values.customerId || undefined);

  const [selectedJobSite, setSelectedJobSite] = useState<JobSiteAutocompleteOption | null>(null);

  const { availableVendors, loading: vendorLoading } = useAvailableVendors();
  // const { availableSpecialFlags, loading: specialFlagLoading } = useAvailableSpecialFlags();
  // const { availableExtraSystems, loading: extraSystemsLoading } = useAvailableExtraSystems();

  return (
    <>
      <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: '12px', fontWeight: 600 }}>
        Job Information
      </Typography>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-around', gap: { xs: '0px', sm: '12px' }, flexDirection: { xs: 'column', sm: 'row' } }}
      >
        <FormikAutocomplete<OptionType, RaptorJobFormValues>
          id="jobTypeId"
          label="Job Type"
          options={
            jobTypes?.data?.map((jobType: JobTypeAutocompleteOption) => ({
              value: jobType.id,
              label: jobType.name ?? 'Unknown Job Type'
            })) ?? []
          }
          optionsloading={jobTypesLoading}
          getOptionLabel={(option: OptionType) => option?.label ?? 'Unknown Job Type'}
          isOptionEqualToValue={(option: OptionType, value: OptionType) => option?.value === value?.value}
          required
          sx={{ flex: 1 }}
          {...tid('jobs:create:job-type-autocomplete')}
        />
        <FormikAutocomplete<JobSiteAutocompleteOption, RaptorJobFormValues>
          id="jobSiteId"
          label="Jobsite"
          options={jobSiteOptions as JobSiteAutocompleteOption[]}
          optionsloading={jobSiteLoading}
          filterOptions={
            jobSiteFilterOptions as (options: JobSiteAutocompleteOption[], state: { inputValue: string }) => JobSiteAutocompleteOption[]
          }
          onInputChange={(_, inputValue, reason) => {
            if (reason !== 'selectOption' && reason !== 'reset') {
              onJobSiteSearchTermChange(inputValue);
            }
          }}
          getOptionLabel={(option) => {
            const address = (option as JobSiteAutocompleteOption).address ? ` - ${(option as JobSiteAutocompleteOption).address}` : '';
            return option.label ? `${option.label}${address}` : 'Unknown JobSite';
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          actions={[
            {
              label: 'View All JobSites',
              icon: <Add />,
              onClick: onOpenCreateJobSiteModal
            },
            {
              label: 'Add New JobSite',
              icon: <Add />,
              onClick: onOpenCreateJobSiteModal
            }
          ]}
          required
          disabled={!values.customerId}
          value={selectedJobSite}
          onChange={(value) => {
            if (Array.isArray(value)) {
              setSelectedJobSite((value[0] as JobSiteAutocompleteOption) || null);
            } else {
              setSelectedJobSite((value as JobSiteAutocompleteOption) || null);
            }
          }}
          sx={{ flex: 1 }}
          {...tid('jobs:create:job-site-autocomplete')}
        />
      </Box>

      <FormikTextField<RaptorJobFormValues> id="name" label="Job Name" testId="jobs:create:job-name-input" />
      <FormikTextField<RaptorJobFormValues> id="jobDescription" label="Job Description" testId="jobs:create:job-description-input" />

      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr' },
          gap: '12px',
          width: '100%'
        }}
      >
        <FormikCheckbox<RaptorJobFormValues> id="cod" label="COD (Cash on Delivery)" />
      </Box>

      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr' },
          gap: '12px',
          width: '100%'
        }}
      >
        <FormikTextField<RaptorJobFormValues> id="poNumber" label="PO Number" testId="jobs:create:po-number-input" />
      </Box>

      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr' },
          gap: '12px',
          width: '100%'
        }}
      >
        <FormikAutocomplete<OptionType, RaptorJobFormValues>
          id="vendorId"
          label="Vendor"
          options={availableVendors.map((vendor) => ({ value: vendor.id, label: vendor.name ?? 'Unknown Vendor' }))}
          optionsloading={vendorLoading}
          filterOptions={(options, state) => {
            const input = state.inputValue.trim().toLowerCase();
            if (!input) return options;
            return options.filter((o) => o.label.toLowerCase().includes(input));
          }}
          getOptionLabel={(option: OptionType) => option.label}
          isOptionEqualToValue={(option: OptionType, value: OptionType) => option.value === value.value}
          sx={{ width: '100%' }}
          {...tid('jobs:create:vendor-autocomplete')}
        />
      </Box>

      {/* <Box>
        <FormikSelectMultiple id="extraSystemIds" label="Extra Systems" options={availableExtraSystems} disabled={extraSystemsLoading} />
      </Box> */}

      {/* <Box>
        <FormikSelect disabled={specialFlagLoading} id="flag1Id" label="Special Flag 1" options={availableSpecialFlags} />
        <FormikSelect disabled={specialFlagLoading} id="flag2Id" label="Special Flag 2" options={availableSpecialFlags} />
        <FormikSelect disabled={specialFlagLoading} id="flag3Id" label="Special Flag 3" options={availableSpecialFlags} />
      </Box> */}
    </>
  );
}
